import { ButtonBase, Skeleton, Stack } from "@mui/material"
import { colors } from "../../services/config/colors"
import arrowLefIcon from "../../assets/icons/arrow-left.svg"
import Title from "../global/common/Title"
import { Dispatch, SetStateAction, useContext } from "react"
import AppearFromSide from "../animations/AppearFromSide"
import Button from "../global/common/Button"
import { MainContext } from "../../controllers/main"
import { useTranslation } from "react-i18next"
import MissionCompletedFeedback from "../feedbacks/common/MissionCompletedFeedback"
import learnMissionIconWhite from "../../assets/icons/missions/missionsTypes/learn-white.svg"
import { MissionPeriod, MissionType } from "../../services/config/enum"
import PointsBadge from "../global/common/PointsBadge"
import Text from "../global/common/Text"
import { QuizzesContext } from "../../controllers/quizzes"
import { useNavigate } from "react-router-dom"
import FadeFromTop from "../animations/FadeFromTop"
import ToggleButton from "../global/common/ToggleButton"

const QuizMobile = ({
  answer,
  setAnswer,
  isAnswerCorrect,
  missionFeedbackOpen,
  onQuizSubmit,
}: {
  answer: string
  setAnswer: Dispatch<SetStateAction<string>>
  isAnswerCorrect: boolean | null
  missionFeedbackOpen: boolean
  onQuizSubmit: () => void
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentMission } = useContext(MainContext)
  const { quizzesLoading, submitLoading, currentQuiz, submitQuiz } =
    useContext(QuizzesContext)

  return !currentMission ? null : (
    <Stack
      style={{
        width: "100%",
        minHeight: "100%",
        backgroundColor: colors.backgroundWhite,
        paddingTop: 22,
        overflowX: "hidden",
        position: "relative",
      }}
    >
      {/* header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={{ width: "100%", height: 48, paddingInline: 16 }}
      >
        <ButtonBase
          style={{
            width: 48,
            minWidth: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 12,
          }}
          onClick={() => {
            navigate("/")
          }}
          aria-label={t("go_back")}
        >
          <img src={arrowLefIcon} style={{ width: 15 }} alt="" />
        </ButtonBase>
        <Title
          fontSize={20}
          lineHeight="23px"
          style={{ height: 23, fontStyle: "italic" }}
          component="h1"
          color={colors.primary}
        >
          {t("learn_mission").toUpperCase()}
        </Title>
        <div
          style={{
            width: 48,
            minWidth: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 12,
          }}
        />
      </Stack>
      {/* mission */}
      <AppearFromSide fade style={{ marginTop: 14, paddingInline: 16 }}>
        <Stack direction="row" gap={0.75}>
          <Stack
            direction="row"
            alignItems="center"
            style={{
              width: "auto",
              height: 32,
              borderRadius: 4,
              paddingInline: 8,
              backgroundColor: colors.missions.learn,
            }}
          >
            <img src={learnMissionIconWhite} style={{ width: 19 }} alt="" />
            <Title
              fontSize={14}
              color={colors.textWhite}
              style={{
                fontVariant: "all-small-caps",
                marginLeft: 8,
                marginBottom: 2,
              }}
            >
              {t("act")}
            </Title>
            <Title
              fontSize={14}
              color={colors.textWhite}
              style={{
                fontVariant: "all-small-caps",
                marginLeft: 14,
                marginBottom: 2,
              }}
            >
              {currentMission.current}/{currentMission.target}
            </Title>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            style={{
              width: "auto",
              height: 32,
              borderRadius: 4,
              paddingInline: 8,
              border: "1px solid " + colors.tertiary,
              backgroundColor: colors.backgroundWhite,
            }}
          >
            <Title fontSize={14} color={colors.tertiary}>
              {currentMission.period === MissionPeriod.DAILY
                ? t("today")
                : currentMission.period === MissionPeriod.WEEKLY
                ? t("week")
                : t("special_singular")}
            </Title>
          </Stack>
          <PointsBadge
            points={currentMission.points}
            pointPerActivity={currentMission.pointPerActivity}
          />
        </Stack>
      </AppearFromSide>
      <AppearFromSide
        fade
        delay={0.05}
        style={{ marginTop: 20, paddingInline: 16 }}
      >
        <Title fontSize={20}>{currentMission.title}</Title>
        {currentMission.description ? (
          <Text fontSize={14} fontWeight={400} style={{ marginTop: 8 }}>
            {currentMission.description}
          </Text>
        ) : null}
      </AppearFromSide>
      {/* actions list */}
      <FadeFromTop
        delay={0.05}
        style={{ marginTop: 20, paddingInline: 16, paddingBottom: 100 }}
      >
        {quizzesLoading || !currentQuiz ? (
          <Stack gap={3} style={{ width: "100%" }}>
            <Stack>
              <Skeleton width="100%" height={24} />
              <Skeleton width="85%" height={24} />
            </Stack>
            <Stack gap={2}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={80}
                style={{ borderRadius: 24 }}
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={80}
                style={{ borderRadius: 24 }}
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={80}
                style={{ borderRadius: 24 }}
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={80}
                style={{ borderRadius: 24 }}
              />
            </Stack>
          </Stack>
        ) : (
          <Stack gap={3} style={{ width: "100%" }}>
            <Title fontSize={18}>{currentQuiz.document.question}</Title>
            <Stack gap={2}>
              <ToggleButton
                option={{ id: "opt1", label: currentQuiz.document.opt1 }}
                onClick={(newValue) => setAnswer(newValue.id)}
                selected={answer === "opt1"}
                success={
                  isAnswerCorrect !== null &&
                  currentQuiz.correctAnswer === "opt1"
                }
                error={
                  answer === "opt1" && isAnswerCorrect === false ? true : false
                }
                disabled={isAnswerCorrect !== null}
                withIcons
              />
              <ToggleButton
                option={{ id: "opt2", label: currentQuiz.document.opt2 }}
                onClick={(newValue) => setAnswer(newValue.id)}
                selected={answer === "opt2"}
                success={
                  isAnswerCorrect !== null &&
                  currentQuiz.correctAnswer === "opt2"
                }
                error={
                  answer === "opt2" && isAnswerCorrect === false ? true : false
                }
                disabled={isAnswerCorrect !== null}
                withIcons
              />
              {currentQuiz.document.opt3 ? (
                <ToggleButton
                  option={{ id: "opt3", label: currentQuiz.document.opt3 }}
                  onClick={(newValue) => setAnswer(newValue.id)}
                  selected={answer === "opt3"}
                  success={
                    isAnswerCorrect !== null &&
                    currentQuiz.correctAnswer === "opt3"
                  }
                  error={
                    answer === "opt3" && isAnswerCorrect === false
                      ? true
                      : false
                  }
                  disabled={isAnswerCorrect !== null}
                  withIcons
                />
              ) : null}
              {currentQuiz.document.opt4 ? (
                <ToggleButton
                  option={{ id: "opt4", label: currentQuiz.document.opt4 }}
                  onClick={(newValue) => setAnswer(newValue.id)}
                  selected={answer === "opt4"}
                  success={
                    isAnswerCorrect !== null &&
                    currentQuiz.correctAnswer === "opt4"
                  }
                  error={
                    answer === "opt4" && isAnswerCorrect === false
                      ? true
                      : false
                  }
                  disabled={isAnswerCorrect !== null}
                  withIcons
                />
              ) : null}
            </Stack>
          </Stack>
        )}
      </FadeFromTop>
      {/* log button */}
      <div
        style={{
          width: "100%",
          paddingTop: 16,
          paddingBottom: 16,
          paddingInline: 16,
          position: "fixed",
          bottom: 0,
          boxShadow: "0px 0px 24px 0px rgba(147, 160, 167, 0.16)",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          backgroundColor: colors.backgroundWhite,
        }}
      >
        <Button
          width="100%"
          loading={submitLoading}
          disabled={!answer}
          onClick={onQuizSubmit}
          title={t("continue")}
        >
          {t("continue")}
        </Button>
      </div>
      {/* feedback */}
      <MissionCompletedFeedback
        open={missionFeedbackOpen}
        missionType={MissionType.LEARN}
        isFailed={isAnswerCorrect === false}
      />
    </Stack>
  )
}

export default QuizMobile
